import { Render } from '@/components/Helpers/ComponentRender/Render';
import { HEALTH_INSIGHT_CARD_BACKGROUND } from '@/constants/images';
import { useKitType } from '@/hooks/useKittype';
import { StandardContainer } from '@design-system/StandardContainer';
import { HealthConnectionsSection } from '@screens/HealthPages/HealthConnectionsSection';
import { HealthZonesSection } from '@screens/HealthPages/HealthZonesSection';
import { LongevitySection } from '@screens/HealthPages/LongevitySection';
import { MicrobiomeProfileSection } from '@screens/HealthPages/MicrobiomeProfileSection';
import { NutritionPlanSection } from '@screens/HealthPages/NutritionPlanSection';
import { ScoreListSection } from '@screens/HealthPages/ScoreListSection';
import { DoctorsNotes } from '@screens/LevelTwoDetail/DoctorsNotes';
import { NutritionJourneyDeeperSection } from '@screens/NutritionPage/NutritionJourneyDeeperSection';
import { JourneyEducationTypes } from '@utils/JourneyEducationTypes';
import {
  IDoctorsNoteResponse,
  ScoreLevel,
} from '@viome/viome-base/src/usecase/Health/Health.model';
import { useGetDoctorsNotes } from '@viome/viome-base/src/usecase/Health/HealthQuery';
import { useState } from 'react';
import EducationVideoCarousel from '../HomePage/EducationVideoCarousel/EducationVideoCarousel';

const GI_SCORE_ID = 1369;
const OHI_SCORE_ID = 1400;

export const HealthContainer = () => {
  const kitType = useKitType();

  const { data: doctorsData } = useGetDoctorsNotes(
    ScoreLevel.L2,
    kitType === 'OHI' ? OHI_SCORE_ID : GI_SCORE_ID,
  );

  const doctorNotes: IDoctorsNoteResponse = doctorsData || [];
  const [healthConnectionLoading, setHealthConnectionLoading] = useState(false);
  const [bgIsLoading, setBgIsLoading] = useState(true);

  const renderDoctorsNoteWithBg = () => {
    if (!doctorNotes) {
      return;
    }
    return (
      <section
        className="bg-black bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) 30%, rgba(0, 0, 0, 1) 100%), url('https://cdn-st.stage.azure.viome.com/viome-cdn/media/app/v2/png/home/image_for_doctors_notes.png')`,
        }}
      >
        <StandardContainer>
          <DoctorsNotes notes={doctorNotes} />
        </StandardContainer>
      </section>
    );
  };

  const renderFBIContent = () => (
    <StandardContainer>
      <HealthZonesSection />

      <LongevitySection />

      <MicrobiomeProfileSection />

      <HealthConnectionsSection
        isQueryLoading={(data) => setHealthConnectionLoading(data)}
      />

      <NutritionPlanSection isLoading={healthConnectionLoading} />
    </StandardContainer>
  );

  const renderGIContent = () => (
    <>
      <StandardContainer>
        <ScoreListSection
          title="My Gut & Digestive Health"
          description="Your scores provide a clear picture of how your gut health influences and supports key areas of your overall well-being."
        />
      </StandardContainer>
      {renderDoctorsNoteWithBg()}
      <StandardContainer>
        <MicrobiomeProfileSection />

        <HealthConnectionsSection
          isQueryLoading={(data) => setHealthConnectionLoading(data)}
        />

        <NutritionPlanSection isLoading={healthConnectionLoading} />
      </StandardContainer>
    </>
  );

  const renderOHIContent = () => (
    <>
      <StandardContainer>
        <ScoreListSection
          title="My Oral Health"
          description="Your scores provide a clear picture of how your oral microbiome influences and supports your gums, teeth, and overall oral cavity."
        />
      </StandardContainer>
      {renderDoctorsNoteWithBg()}
      <StandardContainer>
        <MicrobiomeProfileSection />
        <NutritionPlanSection isLoading={healthConnectionLoading} isOHI />
      </StandardContainer>
    </>
  );

  const renderHIContent = () => (
    <StandardContainer>
      <LongevitySection />

      <HealthZonesSection />

      <MicrobiomeProfileSection />

      <HealthConnectionsSection
        isQueryLoading={(data) => setHealthConnectionLoading(data)}
      />

      <NutritionPlanSection isLoading={healthConnectionLoading} />
    </StandardContainer>
  );

  const contentMap = {
    FBI: renderFBIContent,
    GI: renderGIContent,
    OHI: renderOHIContent,
    HI: renderHIContent,
    PHPRO: renderFBIContent,
  };

  const ContentComponent = contentMap[kitType as keyof typeof contentMap];

  return (
    <div className="relative" data-testid="health-container">
      <StandardContainer>
        <div className="py-0">
          <div className="flex flex-row items-center pt-8">
            <h1 className="dark text-primary typography-d2 relative" data-testid="health-title">Health</h1>
          </div>
        </div>
      </StandardContainer>

      <div className="relative w-full flex justify-center items-center" data-testid="health-backgroundImage-container">
        {bgIsLoading && (
          <div
            className="w-full max-h-[26rem] bg-gray-900 animate-pulse mb-12"
            style={{ height: '26rem' }}
            data-testid="health-backgroundImage-skeleton"
          />
        )}
        <img
          aria-hidden
          src={HEALTH_INSIGHT_CARD_BACKGROUND}
          className={`w-full max-h-[26rem] object-cover ${bgIsLoading ? 'hidden' : ''}`}
          onLoad={() => setBgIsLoading(false)}
          data-testid="health-backgroundImage"
        />
      </div>

      <Render when={kitType}>
        <div data-testid={`health-content-${kitType?.toLowerCase()}`}>
          <ContentComponent />
        </div>
        <StandardContainer>
          <div className="mt-6" data-testid="health-educationVideoCarousel">
            <EducationVideoCarousel screen="health" />
          </div>
          <div className="pb-24" data-testid="health-journeyDeeperSection">
            <NutritionJourneyDeeperSection
              sectionInfo={JourneyEducationTypes.HEALTH_HOME}
              screen="health"
            />
          </div>
        </StandardContainer>
      </Render>
    </div>
  );
};
